import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Alert, Box, Breadcrumbs, Button, PanelCard, Spinner, TextField, Typography } from '@glplus/glpweb-dsl';
import { useTranslation } from 'react-i18next';
import { useVendor } from '../lib/CapabilitiesContext';
import { formatCents, formatShortDate } from '../lib/download';
import { vendorApiClient, type VendorAdjustment } from '../lib/vendorApiClient';

const MUTED = '#5B6573';
const MAX_ATTACHMENT_BYTES = 4 * 1024 * 1024;
const ACCEPTED = new Set(['image/jpeg', 'image/png', 'image/webp', 'application/pdf']);

export default function AdjustmentAppeal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adjustmentId = '' } = useParams();
  const { activeVendorId, has } = useVendor();
  const allowed = has('payout:view');
  const [adjustment, setAdjustment] = useState<VendorAdjustment | null>(null);
  const [text, setText] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!activeVendorId || !allowed) return;
    let alive = true;
    vendorApiClient.listAdjustments(activeVendorId).then((result) => alive && setAdjustment(result.items.find((item) => item.adjustmentId === adjustmentId) ?? null)).catch((cause: Error) => alive && setError(cause.message)).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [activeVendorId, adjustmentId, allowed]);

  if (!allowed) return <Alert severity="warning">{t('payouts.restrictedBody')}</Alert>;
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Spinner color="brand" /></Box>;
  if (!adjustment) return <Alert severity="error">{error ?? t('appeal.notFound')}</Alert>;

  const selectFile = (file: File | undefined) => {
    setError(null);
    if (!file) { setAttachment(null); return; }
    if (!ACCEPTED.has(file.type)) { setError(t('appeal.fileType')); if (fileRef.current) fileRef.current.value = ''; return; }
    if (file.size > MAX_ATTACHMENT_BYTES) { setError(t('appeal.fileSize')); if (fileRef.current) fileRef.current.value = ''; return; }
    setAttachment(file);
  };
  const submit = async () => {
    if (!activeVendorId) return;
    setBusy(true); setError(null);
    try {
      await vendorApiClient.appealAdjustment(activeVendorId, adjustmentId, text, attachment ?? undefined);
      navigate('/payouts', { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('appeal.submitError'));
    } finally { setBusy(false); }
  };

  return <Box data-testid="vendor-adjustment-appeal" sx={{ maxWidth: 780 }}>
    <Breadcrumbs ariaLabel={t('appeal.breadcrumb')} linkComponent={Link} items={[{ label: t('payouts.title'), to: '/payouts' }, { label: t('appeal.title'), ariaCurrent: true }]} />
    <Typography variant="h4" sx={{ mt: 2, fontWeight: 800 }}>{t('appeal.title')}</Typography>
    <Typography sx={{ color: MUTED, mt: 0.5 }}>{t('appeal.subtitle')}</Typography>
    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    <PanelCard as="section" accent="#F67A1A" title={t('appeal.adjustmentTitle')} sx={{ mt: 2, height: 'auto' }}>
      <Typography sx={{ fontSize: 24, fontWeight: 800 }}>{formatCents(adjustment.approvedAmountCents ?? adjustment.proposedAmountCents)}</Typography>
      <Typography sx={{ color: MUTED }}>{adjustment.glpOrderId ?? t('payouts.generalAdjustment')} · {adjustment.location?.name ?? t('appeal.allLocations')} · {formatShortDate(adjustment.reviewedAt ?? adjustment.createdAt)}</Typography>
      <Typography sx={{ mt: 1 }}>{adjustment.reviewNote ?? adjustment.reasonCode.replaceAll('_', ' ')}</Typography>
    </PanelCard>
    <PanelCard as="section" accent="#6D8CA0" title={t('appeal.yourAppeal')} sx={{ mt: 2, height: 'auto' }}>
      <TextField ariaLabel={t('appeal.reason')} label={t('appeal.reason')} value={text} onChange={(event) => setText(event.target.value)} multiline minRows={5} helperText={t('appeal.reasonHelp')} disabled={busy} fullWidth />
      <Box sx={{ mt: 2 }}><Typography sx={{ fontWeight: 700, mb: 0.5 }}>{t('appeal.attachment')}</Typography><Typography variant="caption" sx={{ color: MUTED, display: 'block', mb: 1 }}>{t('appeal.attachmentHelp')}</Typography><Box component="input" ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" aria-label={t('appeal.attachment')} onChange={(event) => selectFile(event.currentTarget.files?.[0])} disabled={busy} />{attachment && <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>{attachment.name}</Typography>}</Box>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}><Button ariaLabel={t('common.cancel')} buttonText={t('common.cancel')} variant="text" onClick={() => navigate('/payouts')} disabled={busy} /><Button ariaLabel={t('appeal.submit')} buttonText={busy ? t('appeal.submitting') : t('appeal.submit')} variant="contained" color="primary" onClick={() => void submit()} disabled={busy || text.trim().length < 10} /></Box>
    </PanelCard>
  </Box>;
}
